import { useEffect, useState } from "react";
import {
  Users,
  Search,
  User,
  Mail,
  Shield,
  ShieldCheck,
  ShieldAlert,
  MoreVertical,
  Loader,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Key,
  ToggleLeft,
  ToggleRight,
  Ban,
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
  MailCheck,
  Award,
} from "lucide-react";
import { adminApi, User as UserType, showSuccess, showError } from "../lib";

const ROLE_COLORS: Record<string, string> = {
  admin: "bg-purple-100 text-purple-800",
  instructor: "bg-blue-100 text-blue-800",
  mentor: "bg-green-100 text-green-800",
  student: "bg-gray-100 text-gray-800",
};

const AdminUsersPage = ({ filterRole }: { filterRole?: string }) => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState(filterRole || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [stats, setStats] = useState<any>(null);
  const [banModalUser, setBanModalUser] = useState<UserType | null>(null);
  const [deleteModalUser, setDeleteModalUser] = useState<UserType | null>(null);
  const [banReason, setBanReason] = useState("");
  const [addUserModal, setAddUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState<UserType | null>(null);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "student",
    password: "",
  });
  const [editUserData, setEditUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [currentPage, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const result = await adminApi.getUsers({
        page: currentPage,
        limit: 12,
        search: searchQuery || undefined,
        role: roleFilter || undefined,
        isActive:
          statusFilter === "active"
            ? true
            : statusFilter === "inactive"
              ? false
              : undefined,
      });

      if (result.success) {
        setUsers(result.data);
        setPagination({
          total: result.pagination?.total || 0,
          pages: result.pagination?.pages || 1,
        });
      }
    } catch (err: any) {
      showError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const result = await adminApi.getStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (err: any) {
      console.error("Failed to load stats");
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleToggleActive = async (user: UserType) => {
    setActionLoading(true);
    try {
      await adminApi.toggleUserActive(user._id);
      showSuccess(
        `User ${user.isActive ? "deactivated" : "activated"} successfully`,
      );
      fetchUsers();
    } catch (err: any) {
      showError(err.message || "Failed to update user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async (user: UserType) => {
    setActionLoading(true);
    try {
      await adminApi.verifyUser(user._id);
      showSuccess("User verified successfully");
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      showError(err.message || "Failed to verify user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async (user: UserType, newRole: string) => {
    setActionLoading(true);
    try {
      await adminApi.changeUserRole(user._id, newRole);
      showSuccess(`Role changed to ${newRole} successfully`);
      fetchUsers();
    } catch (err: any) {
      showError(err.message || "Failed to change role");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = (user: UserType) => {
    setDeleteModalUser(user);
  };

  const confirmDelete = async () => {
    if (!deleteModalUser) return;
    setActionLoading(true);
    try {
      await adminApi.deleteUser(deleteModalUser._id);
      showSuccess("User deleted successfully");
      setDeleteModalUser(null);
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      showError(err.message || "Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBan = (user: UserType) => {
    setBanModalUser(user);
    setBanReason("");
  };

  const confirmBan = async () => {
    if (!banModalUser || !banReason.trim()) {
      showError("Please provide a reason for banning");
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.banUser(banModalUser._id, banReason);
      showSuccess(`User banned successfully`);
      setBanModalUser(null);
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      showError(err.message || "Failed to ban user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnban = async (user: UserType) => {
    if (!confirm(`Are you sure you want to unban ${user.username}?`)) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.unbanUser(user._id);
      showSuccess("User unbanned successfully");
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      showError(err.message || "Failed to unban user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (
      !newUser.firstName ||
      !newUser.lastName ||
      !newUser.email ||
      !newUser.password
    ) {
      showError("Please fill in all fields");
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.createUser(newUser);
      showSuccess("User created successfully");
      setAddUserModal(false);
      setNewUser({
        firstName: "",
        lastName: "",
        email: "",
        role: "student",
        password: "",
      });
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      showError(err.message || "Failed to create user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!editUserModal || !editUserData.email) {
      showError("Please provide valid details");
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.updateUser(editUserModal._id, editUserData);
      showSuccess("User updated successfully");
      setEditUserModal(null);
      fetchUsers();
    } catch (err: any) {
      showError(err.message || "Failed to update user");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.username?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-amber-600 to-orange-500">
                <Users className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                {filterRole === "instructor"
                  ? "Instructors"
                  : filterRole === "mentor"
                    ? "Mentors"
                    : "User Management"}
              </h1>
            </div>
            <button
              onClick={() => setAddUserModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all md:text-xs md:px-3 md:py-2"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Add User
            </button>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Manage platform users and their permissions
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-4">
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
              <div className="flex items-center justify-between mb-1">
                <Users className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-bold text-gray-900">
                  {stats.totalUsers}
                </span>
              </div>
              <p className="text-[9px] text-gray-500">Total Users</p>
            </div>
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
              <div className="flex items-center justify-between mb-1">
                <Shield className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-bold text-gray-900">
                  {stats.totalInstructors}
                </span>
              </div>
              <p className="text-[9px] text-gray-500">Instructors</p>
            </div>
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
              <div className="flex items-center justify-between mb-1">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                <span className="text-sm font-bold text-gray-900">
                  {stats.totalMentors}
                </span>
              </div>
              <p className="text-[9px] text-gray-500">Mentors</p>
            </div>
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
              <div className="flex items-center justify-between mb-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm font-bold text-gray-900">
                  {stats.verifiedUsers}
                </span>
              </div>
              <p className="text-[9px] text-gray-500">Verified</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="p-3 mb-5 bg-white border border-gray-100 shadow-sm rounded-xl">
          <form onSubmit={handleSearch} className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="instructor">Instructor</option>
                <option value="mentor">Mentor</option>
                <option value="student">Student</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <button
                type="submit"
                className="px-3 py-1.5 bg-gradient-to-r from-orange-600 to-orange-500 text-white text-[10px] font-medium rounded-lg hover:shadow-md transition-all"
              >
                Search
              </button>
            </div>
            {(roleFilter || statusFilter || searchQuery) && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setRoleFilter("");
                  setStatusFilter("");
                  setCurrentPage(1);
                }}
                className="text-[9px] text-orange-600 hover:text-orange-700"
              >
                Clear filters
              </button>
            )}
          </form>
        </div>

        {/* Users List - Mobile Card View */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-12 text-center bg-white border border-gray-100 rounded-xl">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-xs text-gray-500">No users found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="space-y-3 md:hidden">
              {filteredUsers.map((user) => (
                <div key={user._id} className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.username}
                          className="object-cover w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-semibold text-gray-900">
                          {user.username}
                        </p>
                        <p className="text-[9px] text-gray-500 truncate max-w-[180px]">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${ROLE_COLORS[user.role] || "bg-gray-100 text-gray-800"}`}
                    >
                      {user.role}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-2 text-[9px]">
                    <span className={`px-1.5 py-0.5 rounded-full ${user.isActive ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                    <span className="flex items-center gap-1">
                      {user.isEmailVerified ? (
                        <MailCheck className="w-3 h-3 text-green-600" />
                      ) : (
                        <ShieldAlert className="w-3 h-3 text-amber-600" />
                      )}
                      <span className="text-gray-500">
                        {user.isEmailVerified ? "Verified" : "Unverified"}
                      </span>
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => setSelectedUser(user)}
                      className="flex-1 p-1.5 text-center text-gray-600 hover:bg-gray-100 rounded text-[9px]"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleToggleActive(user)}
                      className="flex-1 p-1.5 text-center text-gray-600 hover:bg-gray-100 rounded text-[9px]"
                    >
                      {user.isActive ? "Deactivate" : "Activate"}
                    </button>
                    {user.isBlocked ? (
                      <button
                        onClick={() => handleUnban(user)}
                        className="flex-1 p-1.5 text-center text-green-600 hover:bg-green-50 rounded text-[9px]"
                      >
                        Unban
                      </button>
                    ) : (
                      <button
                        onClick={() => handleBan(user)}
                        className="flex-1 p-1.5 text-center text-red-600 hover:bg-red-50 rounded text-[9px]"
                      >
                        Ban
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(user)}
                      className="flex-1 p-1.5 text-center text-red-600 hover:bg-red-50 rounded text-[9px]"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden overflow-hidden bg-white border border-gray-100 shadow-sm md:block rounded-xl">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">User</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Email</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Role</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Status</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Verified</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Joined</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user._id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {user.avatar ? (
                              <img
                                src={user.avatar}
                                alt={user.username}
                                className="object-cover w-6 h-6 rounded-full"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-6 h-6 bg-gray-200 rounded-full">
                                <User className="w-3 h-3 text-gray-500" />
                              </div>
                            )}
                            <span className="font-medium text-gray-900 text-[11px]">
                              {user.username}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-gray-600 text-[10px]">{user.email}</td>
                        <td className="p-3">
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${ROLE_COLORS[user.role] || "bg-gray-100 text-gray-800"}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${user.isActive ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                            {user.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] ${user.isEmailVerified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                            {user.isEmailVerified ? (
                              <MailCheck className="w-2.5 h-2.5" />
                            ) : (
                              <ShieldAlert className="w-2.5 h-2.5" />
                            )}
                            {user.isEmailVerified ? "Verified" : "Unverified"}
                          </span>
                        </td>
                        <td className="p-3 text-gray-500 text-[10px]">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "N/A"}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => setSelectedUser(user)} className="p-1 rounded hover:bg-gray-100" title="View">
                              <Eye className="w-3.5 h-3.5 text-gray-500" />
                            </button>
                            <button onClick={() => handleToggleActive(user)} className="p-1 rounded hover:bg-gray-100" title={user.isActive ? "Deactivate" : "Activate"}>
                              {user.isActive ? (
                                <ToggleRight className="w-3.5 h-3.5 text-green-600" />
                              ) : (
                                <ToggleLeft className="w-3.5 h-3.5 text-red-600" />
                              )}
                            </button>
                            {user.isBlocked ? (
                              <button onClick={() => handleUnban(user)} className="p-1 rounded hover:bg-green-50" title="Unban">
                                <Ban className="w-3.5 h-3.5 text-green-600" />
                              </button>
                            ) : (
                              <button onClick={() => handleBan(user)} className="p-1 rounded hover:bg-red-50" title="Ban">
                                <Ban className="w-3.5 h-3.5 text-red-600" />
                              </button>
                            )}
                            <button onClick={() => handleDelete(user)} className="p-1 rounded hover:bg-red-50" title="Delete">
                              <Trash2 className="w-3.5 h-3.5 text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex flex-col items-center gap-2 mt-5 sm:flex-row sm:justify-between">
            <p className="text-[9px] text-gray-500 order-2 sm:order-1">
              {filteredUsers.length} of {pagination.total} users
            </p>
            <div className="flex items-center order-1 gap-1 sm:order-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="w-3.5 h-3.5 text-gray-600" />
              </button>
              <div className="flex items-center gap-0.5">
                {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                  .filter((p) => {
                    if (pagination.pages <= 5) return true;
                    if (p === 1 || p === pagination.pages) return true;
                    return Math.abs(p - currentPage) <= 1;
                  })
                  .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                    if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) {
                      acc.push("…");
                    }
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === "…" ? (
                      <span key={`ellipsis-${idx}`} className="px-1 text-[9px] text-gray-400">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p as number)}
                        className={`min-w-[26px] h-6 rounded-lg text-[10px] font-medium transition-colors border ${
                          currentPage === p
                            ? "bg-gray-900 text-white border-gray-900"
                            : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
              </div>
              <button
                onClick={() => setCurrentPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={currentPage === pagination.pages}
                className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals - Same as before but with consistent styling */}
      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md bg-white rounded-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-sm font-bold text-gray-900">User Details</h2>
              <button onClick={() => setSelectedUser(null)} className="p-1 rounded hover:bg-gray-100">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                {selectedUser.avatar ? (
                  <img src={selectedUser.avatar} alt={selectedUser.username} className="object-cover w-12 h-12 rounded-full" />
                ) : (
                  <div className="flex items-center justify-center w-12 h-12 bg-gray-200 rounded-full">
                    <User className="w-6 h-6 text-gray-500" />
                  </div>
                )}
                <div>
                  <h5 className="text-sm font-semibold text-gray-900">{selectedUser.username}</h5>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${ROLE_COLORS[selectedUser.role]}`}>
                    {selectedUser.role}
                  </span>
                </div>
              </div>
              <div className="space-y-1 text-[11px]">
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-3.5 h-3.5" />
                  {selectedUser.email}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {!selectedUser.isVerified && (
                  <button onClick={() => { handleVerify(selectedUser); setSelectedUser(null); }} className="flex-1 px-3 py-1.5 bg-green-600 text-white text-[10px] font-medium rounded-lg hover:bg-green-700">
                    Verify User
                  </button>
                )}
                <button onClick={() => handleToggleActive(selectedUser)} className="flex-1 px-3 py-1.5 bg-gray-600 text-white text-[10px] font-medium rounded-lg hover:bg-gray-700">
                  {selectedUser.isActive ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ban Modal */}
      {banModalUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-sm p-5 bg-white rounded-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
                <Ban className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Ban User</h3>
                <p className="text-[10px] text-gray-500">Are you sure you want to ban this user?</p>
              </div>
            </div>
            <textarea
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Reason for banning..."
              className="w-full px-3 py-2 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none"
              rows={3}
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setBanModalUser(null)} className="flex-1 px-3 py-1.5 text-[11px] border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={confirmBan} disabled={actionLoading || !banReason.trim()} className="flex-1 px-3 py-1.5 text-[11px] bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Ban User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-sm p-5 bg-white rounded-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
                <Trash2 className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Delete User</h3>
                <p className="text-[10px] text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModalUser(null)} className="flex-1 px-3 py-1.5 text-[11px] border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={confirmDelete} disabled={actionLoading} className="flex-1 px-3 py-1.5 text-[11px] bg-red-600 text-white rounded-lg hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {addUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md p-5 bg-white rounded-xl">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Add New User</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="First Name" value={newUser.firstName} onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })} className="px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
                <input type="text" placeholder="Last Name" value={newUser.lastName} onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })} className="px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
              </div>
              <input type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
              <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400">
                <option value="student">Student</option>
                <option value="mentor">Mentor</option>
                <option value="instructor">Instructor</option>
              </select>
              <input type="password" placeholder="Password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setAddUserModal(false)} className="flex-1 px-3 py-1.5 text-[11px] border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleAddUser} disabled={actionLoading || !newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password} className="flex-1 px-3 py-1.5 text-[11px] bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-lg hover:shadow-md">
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;